import { takeEvery } from "redux-saga/effects";
import { ENDPOINT } from "../../../config/endpoints";
import {
  applyDiscountApi,
  checkDiscountStatus,
  updateDiscountClosed,
  getExternalDiscounts,
} from "../../slices/DiscountSlice";
import { notifyError } from "../../../effects-bridge";
import { updateSyncedSale } from "../../../helpers/OfflineHelper";
import { commonPostData, applyDiscount, getExternalDiscountsList } from "./shared";

export * from "./shared";

/**
 * WEB half of `redux-store/saga/discount`. `shared.ts` owns `commonPostData` + `applyDiscount`.
 *
 * ⚠️ Every core import names the VARIANT, not the folder (hazard 6/16).
 */

/**
 * Push the closed sale's discount lists to the server, then mark the sale synced locally.
 *
 * ⚠️ **Divergent on two axes.** Web calls `updateSyncedSale` on success — the offline-sync write
 * that landed in core last session. Mobile's equivalent line is COMMENTED OUT in the app source
 * with the note "TODO: offline mode", so mobile never records the sync. Web also surfaces the
 * failure through the injected toast; mobile uses a native alert. See `index.native.ts`.
 */
function* updateDiscount(actions: any): Generator<any, void, any> {
  const data = actions.payload?.data;
  const url = ENDPOINT.DISCOUNT.updateAfterClose(data.mid, data.version);
  let discountData = {
    billDiscountList: [...(data.billDiscountList || [])],
    itemDiscountList: [...(data.itemDiscountList || [])],
  };
  const callback = actions.payload?.callback;
  const response = yield commonPostData(url, JSON.stringify(discountData));
  if (response && !response.code && !response.operation) {
    updateSyncedSale(response);
  } else if (response) {
    notifyError(response.code);
  }
  // decision 95 (approved): a request `commonPostData` swallowed answers nothing; reading its code threw here and the
  // callback never ran, so v2's discount sheet waited for ever. It is called back now whatever came.
  callback && callback(response);
}

/**
 * Poll the manager-approval status for a discount request.
 *
 * ⚠️ **The misspelling `checkStaus` is the app's own function name** — preserved so a `git log -S`
 * still finds it. It is module-private, so nothing outside this file ever referenced the name.
 *
 * ⚠️ **Divergent: web reads `ENDPOINT.DISCOUNT.approvalStatus`, mobile reads
 * `ENDPOINT.MYORDER.approvalStatus`.** Both currently resolve to the same URL
 * (`sale/approval/status`) — web's `index.desktop.ts` endpoint map repeats the key under
 * `DISCOUNT`, while the shared map only carries it under `MYORDER`. Preserved as written, because
 * the two keys are free to diverge later and the apps genuinely read different ones.
 *
 * ⚠️ Divergent again on failure: web toasts, mobile's whole `else` branch is commented out.
 */
function* checkStaus(actions: any): Generator<any, void, any> {
  const url = ENDPOINT.DISCOUNT.approvalStatus();
  const data = actions.payload?.dataToSend;
  const callback = actions.payload?.callback;
  const response = yield commonPostData(url, JSON.stringify(data));
  if (response && response.status) {
    callback && callback(response);
  } else {
    notifyError("Unable To Check Current Status");
  }
}

/**
 * FOUR watchers — and as of 2026-09-08 mobile's `root` has four too. The external-offers
 * watcher was the difference; `packages/core/discounts` made the worker shared.
 *
 * ⚠️ The app source used COMMAS rather than semicolons between the last three `yield`s, making
 * them one comma-expression statement. Behaviour is identical (each `yield` still runs, in order);
 * written as separate statements here, which is what the other uplifted saga roots do.
 */
export default function* root() {
  yield takeEvery(updateDiscountClosed.type, updateDiscount);
  yield takeEvery(applyDiscountApi.type, applyDiscount);
  yield takeEvery(checkDiscountStatus.type, checkStaus);
  yield takeEvery(getExternalDiscounts.type, getExternalDiscountsList);
}
